import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  List,
  ListIcon,
  ListItem,
  Select,
  Text,
  useToast,
} from '@chakra-ui/react';
import { MdCheckCircle, MdErrorOutline, MdPhotoCamera, MdVideocam } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { CourtDto } from '../../../models/court/dto/court.dto';
import { VenueAgentDto } from '../../../models/court-recording/dto/venue-agent.dto';
import { typography } from '../../../theme/tokens';
import { useActivateVenueAgent } from '../hooks/useActivateVenueAgent';
import { useRequestVenueAgentSnapshot } from '../hooks/useRequestVenueAgentSnapshot';
import { useUpdateVenueAgentCameraAssignments } from '../hooks/useUpdateVenueAgentCameraAssignments';
import CameraSnapshot from './CameraSnapshot';
import { aimBlocker, aimPagePath, aimt, describeAimBlocker } from '../utils/camera-aim.utils';
import {
  CameraSetupCard,
  Confirmations,
  assignmentDraft,
  boxStatus,
  cameraSetupCards,
  cst,
  describeBlocker,
  describePicture,
  readConfirmations,
  relativeAgo,
  setupBlockers,
  writeConfirmations,
} from '../utils/camera-setup.utils';
import {
  CameraAssignmentState,
  describeCameraAssignmentState,
  needsActivation,
  vt,
} from '../utils/venue-agent.utils';

interface Props {
  agent: VenueAgentDto;
  organizationId: string;
  courts: CourtDto[];
  isLoadingCourts?: boolean;
}

const STATE_BADGE: Record<CameraAssignmentState, { label: () => string; colorScheme: string }> = {
  applied: { label: () => vt('badge_applied'), colorScheme: 'green' },
  pending: { label: () => vt('badge_pending'), colorScheme: 'orange' },
  unassigned: { label: () => vt('badge_box_config'), colorScheme: 'gray' },
  'no-mac': { label: () => vt('badge_no_mac'), colorScheme: 'gray' },
  'not-reporting': { label: () => vt('badge_not_reporting'), colorScheme: 'red' },
};

const canonical = (map: Record<string, number>): string =>
  Object.keys(map)
    .sort()
    .map((mac) => `${mac}=${map[mac]}`)
    .join(',');

/**
 * Camera → court: the one place where a box's cameras are set up (venue-setup
 * epic, stages 3a–3c on one panel).
 *
 * Per camera: the picture it is taking right now, the court it records, the
 * court the admin wants, and a tick that says "I looked at this picture and
 * it shows this court". Above the rows, "take new photos" asks the box for a
 * fresh frame of every camera (it answers on its next heartbeat, about a
 * minute). Below them, the activation checklist lists what still blocks
 * switching recording on for the players, and the switch itself.
 *
 * The mapping used to live only in the box's config.json, editable over SSH.
 * Saving here writes the admin-authored mapping to the API; the box reads it
 * on its next heartbeat, rewrites its own config and restarts. The same save
 * writes the box's provisioned courts, which is why a tick on a camera the
 * bench pre-assigned (no admin assignment yet) saves the mapping first.
 *
 * What it deliberately does NOT do: add or remove cameras, edit RTSP URLs or
 * credentials — those stay box-local (`add-camera.sh`, `camera-prep.sh`).
 * The row-level "Activate" button in the fleet table stays as the operator's
 * override: the checklist here is the dashboard's own gate, not the API's.
 */
export default function VenueAgentCameraAssignmentsControl({
  agent,
  organizationId,
  courts,
  isLoadingCourts = false,
}: Props) {
  const toast = useToast();
  const navigate = useNavigate();
  const mutation = useUpdateVenueAgentCameraAssignments(organizationId);
  const requestSnapshot = useRequestVenueAgentSnapshot(organizationId);
  const activate = useActivateVenueAgent(organizationId);

  // Re-read every 10 s so "photo taken 2 min ago", "taking…" and the box's
  // online badge move without a reload; the list itself polls separately.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(t);
  }, []);

  const storage = typeof window !== 'undefined' ? window.localStorage : null;
  const [confirmations, setConfirmations] = useState<Confirmations>(() =>
    readConfirmations(storage, agent.id),
  );
  useEffect(() => {
    writeConfirmations(storage, agent.id, confirmations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmations, agent.id]);

  const cards = useMemo(
    () => cameraSetupCards(agent, confirmations, now),
    [agent, confirmations, now],
  );
  const rows = useMemo(() => cards.map((c) => c.row), [cards]);
  const nameById = useMemo(
    () => new Map(courts.map((court) => [court.id, court.name])),
    [courts],
  );
  const courtName = (id: number | null): string =>
    id === null ? '—' : nameById.get(id) ?? vt('unknown_court', { id });

  const saved = useMemo(() => agent.cameraAssignments ?? {}, [agent.cameraAssignments]);

  // Draft = one court per assignable row, seeded from the saved assignment or,
  // failing that, from what the box reports today.
  const seedDraft = (): Record<string, number> => {
    const draft: Record<string, number> = {};
    for (const row of rows) {
      if (!row.mac) continue;
      const seed = row.assignedCourtId ?? row.reportedCourtId;
      if (seed !== null) draft[row.mac] = seed;
    }
    return draft;
  };
  const [draft, setDraft] = useState<Record<string, number>>(seedDraft);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resyncKey = `${canonical(saved)}|${rows.map((r) => `${r.key}:${r.reportedCourtId}`).join(',')}`;
  useEffect(() => {
    setDraft(seedDraft());
    setErrorMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resyncKey]);

  // Dirty = the draft differs from what saving it would leave on the server.
  // A row with no saved assignment whose draft equals the box's own mapping
  // is NOT dirty: saving it would only restate the status quo.
  const isDirty = useMemo(() => {
    for (const row of rows) {
      if (!row.mac) continue;
      const d = draft[row.mac];
      const s = row.assignedCourtId;
      if (s === null) {
        if (d !== undefined && d !== row.reportedCourtId) return true;
      } else if (d !== s) {
        return true;
      }
    }
    return false;
  }, [rows, draft]);

  const duplicateCourt = useMemo(() => {
    const seen = new Map<number, string>();
    for (const [mac, courtId] of Object.entries(draft)) {
      const other = seen.get(courtId);
      if (other) return { courtId, macs: [other, mac] };
      seen.set(courtId, mac);
    }
    return null;
  }, [draft]);

  const assignable = rows.filter((r) => r.mac !== null);
  const status = boxStatus(agent, now);
  const blockers = useMemo(() => setupBlockers(agent, cards, now), [agent, cards, now]);
  const photosPending = cards.some((c) => c.picture.kind === 'waiting');
  const revoked = Boolean(agent.revokedAt);

  const saveAssignments = async (assignments: Record<string, number>): Promise<boolean> => {
    setErrorMessage(null);
    try {
      await mutation.mutateAsync({ agentId: agent.id, assignments });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setErrorMessage(message);
      toast({ title: vt('assignments_save_failed'), description: message, status: 'error' });
      return false;
    }
  };

  const handleSave = async (): Promise<void> => {
    // A changed court invalidates the tick for every camera whose court moved:
    // the picture has to be looked at again against the new name.
    const moved = cards.filter((c) => c.row.mac && draft[c.row.mac] !== c.courtId).map((c) => c.key);
    if (!(await saveAssignments(draft))) return;
    if (moved.length > 0) {
      setConfirmations((prev) => {
        const next = { ...prev };
        for (const key of moved) delete next[key];
        return next;
      });
    }
    toast({
      title: vt('assignments_saved'),
      description: vt('assignments_saved_desc'),
      status: 'success',
      duration: 4000,
    });
  };

  const handleConfirm = async (card: CameraSetupCard, checked: boolean): Promise<void> => {
    if (!checked) {
      setConfirmations((prev) => {
        const next = { ...prev };
        delete next[card.key];
        return next;
      });
      return;
    }
    if (card.courtId === null) return;
    // A camera with no admin assignment yet (the bench's own config) is saved
    // on confirm, so the tick also writes the box's provisioned courts.
    if (card.row.assignedCourtId === null && card.row.mac) {
      const ok = await saveAssignments(
        assignmentDraft(cards, { key: card.key, courtId: card.courtId }),
      );
      if (!ok) return;
    }
    setConfirmations((prev) => ({ ...prev, [card.key]: card.courtId as number }));
  };

  const handleTakePhotos = async (): Promise<void> => {
    try {
      await requestSnapshot.mutateAsync(agent.id);
      // Re-read the clock so the rows flip to "taking…" on this render, not
      // on the next 10-second tick.
      setNow(Date.now());
      toast({
        title: cst('photos_requested_toast'),
        description: cst('photos_requested_toast_desc'),
        status: 'success',
        duration: 4000,
      });
    } catch (err) {
      toast({
        title: cst('photos_request_failed'),
        description: err instanceof Error ? err.message : String(err),
        status: 'error',
      });
    }
  };

  const handleActivate = async (): Promise<void> => {
    if (!window.confirm(cst('activate_confirm'))) return;
    try {
      await activate.mutateAsync(agent.id);
      toast({ title: cst('activated_toast'), status: 'success' });
    } catch (err) {
      toast({
        title: cst('activate_failed'),
        description: err instanceof Error ? err.message : String(err),
        status: 'error',
      });
    }
  };

  const lastSeenAgo = agent.lastSeenAt ? relativeAgo(agent.lastSeenAt, now) : '';
  const boxLine =
    status === 'online'
      ? { badge: cst('box_online'), color: 'green', hint: cst('box_online_hint', { ago: lastSeenAgo }) }
      : status === 'offline'
        ? { badge: cst('box_offline'), color: 'red', hint: cst('box_offline_hint', { ago: lastSeenAgo }) }
        : { badge: cst('box_never_seen'), color: 'gray', hint: cst('box_never_seen_hint') };

  return (
    <FormControl>
      <Flex alignItems="center" gap={3} wrap="wrap" mb={1}>
        <FormLabel {...typography.formLabel} mb={0}>
          {vt('camera_to_court')}
        </FormLabel>
        {!revoked && (
          <Badge colorScheme={boxLine.color} fontSize="2xs" data-testid="box-status">
            {boxLine.badge}
          </Badge>
        )}
        {!revoked && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<MdPhotoCamera />}
            isDisabled={status !== 'online' || photosPending}
            isLoading={requestSnapshot.isLoading}
            onClick={() => void handleTakePhotos()}
          >
            {photosPending ? cst('taking_photos') : cst('take_photos')}
          </Button>
        )}
      </Flex>
      {!revoked && (
        <Text fontSize="xs" color="gray.500" mb={3}>
          {boxLine.hint}
        </Text>
      )}

      {cards.length === 0 ? (
        <Text fontSize="sm" color="gray.500" data-testid="camera-assignments-empty">
          {vt('no_cameras')}
        </Text>
      ) : (
        <Flex direction="column" gap={3} data-testid="camera-assignments-rows">
          {cards.map((card) => {
            const row = card.row;
            const badge = STATE_BADGE[row.state];
            const value = row.mac ? draft[row.mac] ?? '' : row.reportedCourtId ?? '';
            // A tick is about the SAVED mapping: with an unsaved change in the
            // draft, the name next to the box is not the one the box applies.
            const confirmEnabled = card.canConfirm && !isDirty && !revoked;
            // "Viser en direct": the box online, the camera streaming, a MAC to ask for.
            const aimBlock = aimBlocker(agent, row, now);
            return (
              <Box
                key={row.key}
                borderWidth="1px"
                borderRadius="md"
                p={3}
                data-testid={`camera-row-${row.key}`}
              >
                <Flex alignItems="center" gap={3} wrap="wrap">
                  <Box w="200px" flexShrink={0}>
                    <CameraSnapshot
                      agentId={agent.id}
                      organizationId={organizationId}
                      subject={card.subject}
                      lastSnapshotAt={card.picture.kind === 'fresh' ? card.picture.takenAt : undefined}
                      placeholder={describePicture(card.picture, now)}
                      failedText={cst('picture_failed')}
                      alt={vt('snapshot_alt', { camera: row.mac ?? row.key })}
                      height="112px"
                    />
                    <Text fontSize="xs" color="gray.500" mt={1} data-testid={`picture-${row.key}`}>
                      {describePicture(card.picture, now)}
                    </Text>
                    {!revoked && (
                      <Box mt={2}>
                        <Button
                          size="sm"
                          variant="outline"
                          w="100%"
                          leftIcon={<MdVideocam />}
                          isDisabled={aimBlock !== null}
                          onClick={() => {
                            if (row.mac) navigate(aimPagePath(agent.id, row.mac));
                          }}
                          data-testid={`aim-${row.key}`}
                        >
                          {aimt('aim_live')}
                        </Button>
                        {aimBlock && (
                          <Text fontSize="xs" color="gray.500" mt={1} data-testid={`aim-blocked-${row.key}`}>
                            {describeAimBlocker(aimBlock)}
                          </Text>
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box minW="180px">
                    <Text fontSize="xs" color="gray.500">
                      {vt('camera')}
                    </Text>
                    <Text fontSize="sm" fontFamily="mono">
                      {row.mac ?? vt('mac_unknown')}
                    </Text>
                    {row.connected !== null && (
                      <Text fontSize="xs" color={row.connected ? 'green.600' : 'red.500'}>
                        {row.connected ? vt('connected') : vt('disconnected')}
                      </Text>
                    )}
                  </Box>
                  <Box minW="140px">
                    <Text fontSize="xs" color="gray.500">
                      {vt('recording_now')}
                    </Text>
                    <Text fontSize="sm">{courtName(row.reportedCourtId)}</Text>
                  </Box>
                  <Box flex="1" minW="200px">
                    <Text fontSize="xs" color="gray.500">
                      {vt('assign_to')}
                    </Text>
                    <Select
                      size="sm"
                      aria-label={vt('court_for_camera', { camera: row.mac ?? row.key })}
                      value={value}
                      isDisabled={!row.mac || revoked || mutation.isLoading || isLoadingCourts}
                      onChange={(event) => {
                        if (!row.mac) return;
                        const next = Number.parseInt(event.target.value, 10);
                        setDraft((prev) => ({ ...prev, [row.mac as string]: next }));
                      }}
                    >
                      {row.reportedCourtId !== null && !nameById.has(row.reportedCourtId) && (
                        <option value={row.reportedCourtId}>
                          {vt('unknown_court', { id: row.reportedCourtId })}
                        </option>
                      )}
                      {courts.map((court) => (
                        <option key={court.id} value={court.id}>
                          {court.name}
                        </option>
                      ))}
                    </Select>
                  </Box>
                  <Badge colorScheme={badge.colorScheme} fontSize="2xs">
                    {badge.label()}
                  </Badge>
                </Flex>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  {describeCameraAssignmentState(row.state)}
                </Text>
                {card.courtId !== null && !revoked && (
                  <Box mt={2}>
                    <Checkbox
                      size="md"
                      colorScheme="green"
                      isChecked={card.confirmed}
                      isDisabled={!confirmEnabled || mutation.isLoading}
                      onChange={(e) => void handleConfirm(card, e.target.checked)}
                      data-testid={`confirm-${card.key}`}
                    >
                      <Text fontSize="sm">{cst('confirm_label', { court: courtName(card.courtId) })}</Text>
                    </Checkbox>
                    {!confirmEnabled && (
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        {isDirty && card.canConfirm ? cst('confirm_save_first') : cst('confirm_blocked_hint')}
                      </Text>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Flex>
      )}

      <FormHelperText fontSize="xs">
        {vt('camera_helper')}
      </FormHelperText>

      {duplicateCourt && (
        <Alert status="error" mt={3} borderRadius="md" fontSize="sm" data-testid="camera-assignments-duplicate">
          <AlertIcon />
          <Text>
            {vt('duplicate_court', { court: courtName(duplicateCourt.courtId) })}
          </Text>
        </Alert>
      )}

      {errorMessage && (
        <Alert status="error" mt={3} borderRadius="md" fontSize="sm">
          <AlertIcon />
          <Text>{errorMessage}</Text>
        </Alert>
      )}

      <Flex mt={3} gap={2} alignItems="center">
        <Button
          size="sm"
          colorScheme="brand"
          isDisabled={
            !isDirty || duplicateCourt !== null || assignable.length === 0 || revoked
          }
          isLoading={mutation.isLoading}
          onClick={() => void handleSave()}
        >
          {vt('save_assignments')}
        </Button>
        {isDirty && (
          <Button
            size="sm"
            variant="ghost"
            isDisabled={mutation.isLoading}
            onClick={() => {
              setDraft(seedDraft());
              setErrorMessage(null);
            }}
          >
            {vt('reset')}
          </Button>
        )}
      </Flex>

      {/* The switch — only while the box still needs activating (or to say it is on). */}
      {!revoked && (
        <Box borderWidth="1px" borderRadius="md" p={3} mt={4} data-testid="activation-panel">
          <Text {...typography.formLabel} mb={1}>
            {cst('activation_title')}
          </Text>
          {agent.activatedAt ? (
            <>
              <Text fontSize="sm" color="green.600" fontWeight="600">
                {cst('recording_active', { date: new Date(agent.activatedAt).toLocaleDateString() })}
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {cst('recording_active_hint')}
              </Text>
            </>
          ) : (
            <>
              <Text fontSize="xs" color="gray.500" mb={2}>
                {cst('activation_intro')}
              </Text>
              <List spacing={1} mb={3} data-testid="activation-blockers">
                {blockers.length === 0 ? (
                  <ListItem fontSize="sm">
                    <ListIcon as={MdCheckCircle} color="green.500" />
                    {cst('all_good')}
                  </ListItem>
                ) : (
                  blockers.map((b) => (
                    <ListItem key={b} fontSize="sm">
                      <ListIcon as={MdErrorOutline} color="orange.500" />
                      {describeBlocker(b)}
                    </ListItem>
                  ))
                )}
              </List>
              <Flex alignItems="center" gap={3} wrap="wrap">
                <Button
                  size="sm"
                  colorScheme="brand"
                  isDisabled={blockers.length > 0 || !needsActivation(agent)}
                  isLoading={activate.isLoading}
                  onClick={() => void handleActivate()}
                >
                  {cst('activate')}
                </Button>
                <Text fontSize="xs" color="gray.500">
                  {cst('activation_override_hint')}
                </Text>
              </Flex>
            </>
          )}
        </Box>
      )}
    </FormControl>
  );
}
